import React, { useCallback, useRef, useState } from 'react';
import { ACCEPT, uploadFile, waitIndexed, formatSize } from '../api/files';
import { useChatUi } from '../ChatContext';
import '../styles/files.css';

/**
 * Attaching files from the message box. The files go to the user's own workspace (private to them) and are then
 * searched with the manuals on every question. useUploads() holds the in-flight uploads; AttachButton is the
 * paperclip in the composer; UploadTray shows each file: uploading % -> indexing -> ready / stored / failed.
 */
export function useUploads() {
  const [items, setItems] = useState([]);
  const patch = (key, fields) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...fields } : i)));
  const dismiss = useCallback((key) => setItems((list) => list.filter((i) => i.key !== key)), []);

  const add = useCallback(async (fileList) => {
    for (const file of Array.from(fileList || [])) {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setItems((list) => [...list, { key, name: file.name, size: file.size, state: 'uploading', progress: 0 }]);
      try {
        const row = await uploadFile(file, (progress) => patch(key, { progress }));
        if (row.duplicate) {
          patch(key, { state: row.status === 'ready' ? 'ready' : row.status, note: 'already in your files' });
          continue;
        }
        patch(key, { state: 'indexing', id: row.id });
        const [done] = await waitIndexed([row.id]);
        patch(key, { state: done ? done.status : 'failed', note: done && done.error });
        if (done && done.status === 'ready') setTimeout(() => dismiss(key), 8000);
      } catch (e) {
        patch(key, { state: 'failed', note: (e.response && e.response.data && e.response.data.detail) || e.message });
      }
    }
  }, [dismiss]);

  return { items, add, dismiss };
}

export function AttachButton({ onFiles, disabled }) {
  const { t } = useChatUi();
  const input = useRef(null);
  return (
    <>
      <button type="button" className="cv-attach" title={t('Attach files to your workspace')} aria-label={t('Attach files to your workspace')}
        onClick={() => input.current && input.current.click()} disabled={disabled}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21.4 11.1l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" />
        </svg>
      </button>
      <input ref={input} type="file" multiple accept={ACCEPT} hidden
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
    </>
  );
}

const LABELS = {
  uploading: 'Uploading', indexing: 'Reading the file…', ready: 'Ready — ask about it', stored: 'Saved (not searchable)',
  failed: 'Failed', processing: 'Reading the file…',
};

export function UploadTray({ uploads }) {
  const { t } = useChatUi();
  if (!uploads.items.length) return null;
  return (
    <div className="cv-upload-tray" aria-live="polite">
      {uploads.items.map((u) => (
        <div key={u.key} className={`cv-upload is-${u.state}`} title={u.note || ''}>
          <span className="cv-upload__icon" aria-hidden="true">
            {u.state === 'ready' ? '✓' : u.state === 'failed' ? '!' : u.state === 'stored' ? '•' : <span className="cv-spinner" />}
          </span>
          <span className="cv-upload__name">{u.name}</span>
          <span className="cv-upload__state">
            {u.state === 'uploading' ? `${t(LABELS.uploading)} ${u.progress || 0}%` : t(LABELS[u.state] || u.state)}
            {u.note && u.state !== 'failed' ? ` · ${t(u.note)}` : ''}
            {u.state === 'failed' && u.note ? ` · ${u.note}` : ''}
            {u.state === 'uploading' ? '' : ` · ${formatSize(u.size)}`}
          </span>
          <button type="button" className="cv-upload__x" aria-label={t('Dismiss')} onClick={() => uploads.dismiss(u.key)}>×</button>
        </div>
      ))}
    </div>
  );
}
